import { ReactElement, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '../../../redux/store';
import { useFormik } from 'formik';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { authActions } from '../../../redux/slices/auth.slice';
import { ROUTES } from '../../../helpers/constans';
import { registrationSchema } from '../../../helpers/formik-shemas/auth.schema';
import AuthLayout from '../components/auth-layout';
import AuthTextField from '../components/auth-text-field';
import { styles } from '../components/auth-layout.styles';

interface IFormValue {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

const Registration = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken: string | undefined = useSelector((state: RootState) => state.auth.token);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);
  const errorMessage = useSelector((state: RootState) => state.auth.errorMessage);

  const formik = useFormik<IFormValue>({
    initialValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
    },
    validationSchema: registrationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: ({ name, email, password }) => {
      dispatch(authActions.registration({ name, email, password }));
    },
  });

  useEffect((): void => {
    if (authToken) {
      navigate(ROUTES.home);
    }
  }, [authToken, navigate]);

  useEffect(() => {
    return () => {
      dispatch(authActions.clearAuthFeedback());
    };
  }, [dispatch]);

  const termsError = Boolean(formik.touched.acceptTerms && formik.errors.acceptTerms);

  return (
    <AuthLayout title="Create your account" subtitle="Start managing digital objects with a secure XDOS workspace.">
      <Box
        component="form"
        sx={styles.form}
        onSubmit={formik.handleSubmit}
        noValidate
        aria-busy={isLoading}
      >
        {errorMessage ? (
          <Alert severity="error" sx={styles.alert} role="alert">
            {errorMessage}
          </Alert>
        ) : null}

        <AuthTextField
          name="name"
          label="Full name"
          autoComplete="name"
          formik={formik}
          disabled={isLoading}
          icon={<PersonOutlineIcon fontSize="small" sx={styles.fieldIcon} />}
        />
        <AuthTextField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          formik={formik}
          disabled={isLoading}
          icon={<MailOutlineIcon fontSize="small" sx={styles.fieldIcon} />}
        />
        <AuthTextField
          name="password"
          label="Password"
          type="password"
          autoComplete="new-password"
          formik={formik}
          disabled={isLoading}
          icon={<LockOutlinedIcon fontSize="small" sx={styles.fieldIcon} />}
        />
        <AuthTextField
          name="confirmPassword"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          formik={formik}
          disabled={isLoading}
          icon={<LockOutlinedIcon fontSize="small" sx={styles.fieldIcon} />}
        />

        <Box>
          <FormControlLabel
            sx={styles.checkbox}
            control={
              <Checkbox
                name="acceptTerms"
                checked={formik.values.acceptTerms}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isLoading}
                inputProps={{ 'aria-describedby': termsError ? 'acceptTerms-error' : undefined }}
              />
            }
            label={
              <span>
                I agree to the{' '}
                <Typography component={Link} to={ROUTES.terms} target="_blank" sx={styles.textLink}>
                  Terms
                </Typography>{' '}
                and{' '}
                <Typography component={Link} to={ROUTES.privacy} target="_blank" sx={styles.textLink}>
                  Privacy Policy
                </Typography>
              </span>
            }
          />
          {termsError ? (
            <FormHelperText id="acceptTerms-error" error>
              {formik.errors.acceptTerms}
            </FormHelperText>
          ) : null}
        </Box>

        <Button type="submit" sx={styles.submit} disabled={isLoading} fullWidth>
          {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Create account'}
        </Button>
      </Box>

      <Typography sx={styles.footer}>
        Already have an account?{' '}
        <Typography component={Link} to={ROUTES.login} sx={styles.textLink}>
          Sign in
        </Typography>
      </Typography>
    </AuthLayout>
  );
};

export default Registration;
